import React, { useState } from 'react';
import {
  Tr,
  Td,
  Text,
  IconButton,
  Image,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  VStack,
  Divider,
  Box,
} from '@chakra-ui/react';
import { MdShoppingCart } from 'react-icons/md';
import { useAppDispatch } from '../../../utils/reduxHooks';
import { addItemsAction } from '../../../modules/cart/actions';

interface FiltersProductCardProps {
  product: {
    id: string;
    supplierId: string;
    brand: string;
    brand_article: string;
    name: string;
    amount: number;
    delivery_term: number;
    price: number;
    imageUrl?: string; // Ссылка на изображение, если доступна
  };
}

export const FiltersProductCard: React.FC<FiltersProductCardProps> = ({ product }) => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false); // Состояние модального окна
  const [quantity, setQuantity] = useState(1); // Количество
  const [comment, setComment] = useState(''); // Комментарий

  // Форматирование цены для отображения
  const formatPrice = (price: number) =>
    parseFloat(String(price))
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => setIsOpen(false);

  const handleAddToCart = () => {
    const itemToAdd = {
      article_number: product.brand_article,
      supplier: product.supplierId,
      brand: product.brand,
      amount: quantity,
      price: parseFloat(String(product.price)),
      delivery_term: product.delivery_term,
      good_name: product.name,
      comment: comment,
    };

    dispatch(addItemsAction.request(itemToAdd));
    handleCloseModal();
    setComment('');
    setQuantity(1);
  };

  // Общая стоимость
  const totalPrice = formatPrice(quantity * product.price);

  return (
    <>
      <Tr>
        <Td>
          <HStack>
            {product.imageUrl && <Image src={product.imageUrl} alt={product.name} boxSize="30px" />}
            <Text fontWeight="bold">{product.brand}</Text>
          </HStack>
          <Text color="gray.500">{product.brand_article}</Text>
        </Td>
        <Td>
          <Text>{product.name}</Text>
        </Td>
        <Td isNumeric>{product.amount || '—'}</Td>
        <Td>{product.delivery_term !== -1 ? `${product.delivery_term} дн.` : '—'}</Td>
        <Td fontWeight="bold" isNumeric whiteSpace="nowrap">
          {formatPrice(product.price)} ₽
        </Td>
        <Td>
          <IconButton
            aria-label="Добавить в корзину"
            colorScheme="teal"
            variant="outline"
            size="sm"
            onClick={handleOpenModal} // Открываем модалку
          />
          <></>
        </Td>
      </Tr>

      {/* Модальное окно */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Добавить в корзину</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" fontSize="lg">
                  {product.brand} {product.brand_article}
                </Text>
                <Text color="gray.600" fontSize="sm" noOfLines={3}>
                  {product.name}
                </Text>
              </Box>

              {/* Срок поставки */}
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={1}>
                  Срок поставки
                </Text>
                <Text fontSize="md">
                  {product.delivery_term !== -1 ? `${product.delivery_term} дн.` : '—'}
                </Text>
              </Box>

              <Divider />

              {/* Количество */}
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Количество
                </Text>
                <HStack justifyContent="space-between">
                  {/* Input для количества */}
                  <NumberInput
                    value={quantity}
                    min={1}
                    max={product.amount}
                    onChange={valueString => setQuantity(parseInt(valueString, 10) || 1)}
                    size="md"
                    width="50%"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>

                  {/* Общая цена */}
                  <Text fontWeight="bold" fontSize="lg">
                    {totalPrice} ₽
                  </Text>
                </HStack>
                <Text color="gray.500" fontSize="sm">
                  В наличии {product.amount} шт.
                </Text>
              </Box>

              {/* Комментарий */}
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Комментарий к заказу
                </Text>
                <Textarea
                  placeholder="Прошу связаться со мной для уточнения деталей"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleCloseModal}>
              Отмена
            </Button>
            <Button colorScheme="teal" onClick={handleAddToCart}>
              Добавить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
